/**
 * PeopleBrowsePage — public people discovery + filter UI.
 *
 * Faceted browse over the 1500-person catalogue:
 *   - Search input
 *   - Category chips (athlete, musician, …) with counts
 *   - Decade chips (1950s, 1960s, … 2010s)
 *   - Country chips (top 30)
 *   - Country chips
 *
 * Each card shows real Wikipedia photo + name + DOB + country.
 * Click a card → opens the existing /person/:id detail page.
 *
 * Public route at /people (no auth) so press / casual visitors can browse
 * the catalogue without signing up.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowLeft, ChevronDown } from 'lucide-react';
import axios from 'axios';
import EntityAvatar from '../components/EntityAvatar';
import { VirtuosoGrid } from 'react-virtuoso';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 36;

const CATEGORY_LABELS = {
  athlete:        'Athletes',
  actor:          'Actors',
  musician:       'Musicians',
  ceo:            'CEOs',
  country_leader: 'Rulers',
  influencer:     'Influencers',
};

const COUNTRY_FLAG = {
  'United States':  '🇺🇸', 'United Kingdom': '🇬🇧', 'Canada': '🇨🇦',
  'Spain': '🇪🇸', 'Japan': '🇯🇵', 'France': '🇫🇷', 'Germany': '🇩🇪',
  'Italy': '🇮🇹', 'Brazil': '🇧🇷', 'Argentina': '🇦🇷', 'Mexico': '🇲🇽',
  'India': '🇮🇳', 'China': '🇨🇳', 'South Korea': '🇰🇷', 'Australia': '🇦🇺',
  'Sweden': '🇸🇪', 'Netherlands': '🇳🇱', 'Russia': '🇷🇺', 'Portugal': '🇵🇹',
  'Belgium': '🇧🇪', 'Switzerland': '🇨🇭', 'Poland': '🇵🇱', 'Norway': '🇳🇴',
  'Greece': '🇬🇷', 'Turkey': '🇹🇷', 'Israel': '🇮🇱', 'Ireland': '🇮🇪',
  'Denmark': '🇩🇰', 'Austria': '🇦🇹', 'Finland': '🇫🇮',
};

function Chip({ active, label, count, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={[
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-secondary/50 border-border/60 text-muted-foreground hover:bg-secondary/80 hover:text-foreground',
      ].join(' ')}
    >
      {label}
      {count != null && (
        <span className={active ? 'opacity-80' : 'opacity-60'}>{count}</span>
      )}
    </button>
  );
}

function PersonCard({ entity, onClick }) {
  const md = entity.metadata || {};
  const yearOnly = md.birth_date ? md.birth_date.slice(0, 4) : null;
  const flag = COUNTRY_FLAG[md.country] || '';
  return (
    <button
      onClick={onClick}
      data-testid={`person-card-${entity.id}`}
      className="group w-full h-full flex flex-col items-center text-center p-3 rounded-xl border border-border/40 bg-card/50 hover:border-primary/50 hover:bg-card/80 transition-all"
    >
      <EntityAvatar entity={entity} size={80} className="ring-2 ring-border/40 group-hover:ring-primary/40 transition-shadow" />
      <div className="mt-3 w-full min-w-0">
        <div
          className="text-sm font-semibold truncate"
          style={{ fontFamily: 'var(--font-heading)' }}
          title={entity.name}
        >
          {entity.name}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
          {[yearOnly, flag && `${flag} ${md.country}`].filter(Boolean).join(' · ')}
        </div>
        <div className="text-[10px] text-muted-foreground/70 mt-0.5 uppercase tracking-wider">
          {CATEGORY_LABELS[entity.entity_type] || entity.entity_type}
        </div>
      </div>
    </button>
  );
}

export default function PeopleBrowsePage({ onBack }) {
  const navigate = useNavigate();

  const [facets, setFacets] = useState({ countries: [], decades: [], categories: [] });
  const [search, setSearch]   = useState('');
  const [category, setCategory] = useState(null);
  const [decade, setDecade]   = useState(null);
  const [country, setCountry] = useState(null);

  const [entities, setEntities] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  // 2026-07-20 launch perf — VirtuosoGrid virtualizes the card grid against
  // the page's own scroll container (set via callback ref below).
  const [gridScrollParent, setGridScrollParent] = useState(null);

  // Load facets once
  useEffect(() => {
    axios.get(`${API}/api/entities/facets`).then(r => setFacets(r.data)).catch(() => {});
  }, []);

  // Debounced search query
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Build query string
  const queryStr = useMemo(() => {
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), sort_by: 'name', sort_dir: '1' });
    if (debouncedSearch) p.set('search', debouncedSearch);
    if (category) p.set('entity_type', category);
    if (decade != null) p.set('decade', String(decade));
    if (country) p.set('country', country);
    return p.toString();
  }, [debouncedSearch, category, decade, country]);

  // Reset pagination when filters change
  useEffect(() => { setSkip(0); }, [queryStr]);

  // Fetch entities
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/api/entities?${queryStr}&skip=${skip}`)
      .then(r => {
        if (cancelled) return;
        setEntities(prev => (skip === 0 ? r.data.entities : [...prev, ...r.data.entities]));
        setTotal(r.data.total || 0);
      })
      .catch(() => !cancelled && setEntities([]))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [queryStr, skip]);

  const clearAll = useCallback(() => {
    setSearch(''); setCategory(null); setDecade(null); setCountry(null);
  }, []);
  const anyFilter = !!(search || category || decade != null || country);

  return (
    <div className="min-h-screen bg-background" data-testid="people-browse-page">
      <div className="h-screen overflow-y-auto" ref={setGridScrollParent}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5">
          {/* Header */}
          {/* 2026-09-07 — direct /people visits (from the sign-in link) get a
              way back too. */}
          <button
            onClick={onBack || (() => navigate('/'))}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-3"
            data-testid="people-back"
          >
            <ArrowLeft className="h-4 w-4" /> {onBack ? 'Back' : 'Back to sign in'}
          </button>
          <h1
            className="text-3xl sm:text-4xl font-bold mb-1"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Browse People
          </h1>
          <p className="text-sm text-muted-foreground mb-5">
            {total.toLocaleString()} draftable people across {facets.categories.length} categories.
          </p>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…"
              className="w-full pl-9 pr-9 py-2.5 rounded-lg border border-border/60 bg-card/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="people-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary/60 rounded">
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>

          {/* Category chips */}
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            {facets.categories.map(c => (
              <Chip
                key={c.value}
                label={CATEGORY_LABELS[c.value] || c.value}
                count={c.count}
                active={category === c.value}
                onClick={() => setCategory(category === c.value ? null : c.value)}
                testid={`chip-category-${c.value}`}
              />
            ))}
          </div>

          {/* Decade chips */}
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground self-center mr-1">Born</span>
            {facets.decades.map(d => (
              <Chip
                key={d.value}
                label={`${d.value}s`}
                count={d.count}
                active={decade === d.value}
                onClick={() => setDecade(decade === d.value ? null : d.value)}
                testid={`chip-decade-${d.value}`}
              />
            ))}
          </div>

          {/* Country chips */}
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground self-center mr-1">From</span>
            {facets.countries.slice(0, 20).map(c => (
              <Chip
                key={c.value}
                label={`${COUNTRY_FLAG[c.value] || ''} ${c.value}`.trim()}
                count={c.count}
                active={country === c.value}
                onClick={() => setCountry(country === c.value ? null : c.value)}
                testid={`chip-country-${c.value}`}
              />
            ))}
          </div>

          {/* Clear-all row (sex filter removed 2026-09-01, owner request) */}
          {anyFilter && (
            <div className="flex mb-4">
              <button
                onClick={clearAll}
                className="ml-auto text-xs text-muted-foreground hover:text-foreground underline"
                data-testid="people-clear"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Result count */}
          <p className="text-xs text-muted-foreground mb-3">
            {loading && skip === 0
              ? 'Loading…'
              : `Showing ${entities.length} of ${total.toLocaleString()}`}
          </p>

          {/* Grid — virtualized (react-virtuoso VirtuosoGrid) */}
          {gridScrollParent && (
            <VirtuosoGrid
              customScrollParent={gridScrollParent}
              totalCount={entities.length}
              overscan={400}
              listClassName="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
              itemContent={(index) => {
                const e = entities[index];
                if (!e) return null;
                return (
                  <PersonCard
                    entity={e}
                    onClick={() => navigate(`/person/${e.id}`)}
                  />
                );
              }}
            />
          )}

          {/* Load more — parity with Rankings tab (full-width outline). */}
          {entities.length < total && (
            <div className="mt-6 mb-12">
              <button
                onClick={() => setSkip(s => s + PAGE_SIZE)}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-1 h-10 px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50"
                data-testid="people-load-more"
              >
                {loading ? 'Loading…' : (<><ChevronDown className="h-4 w-4" /> Load {Math.min(PAGE_SIZE, total - entities.length)} more</>)}
              </button>
            </div>
          )}

          {/* Attribution + opt-out (legal — see CHANGELOG v5.7.23) */}
          <div className="text-xs text-muted-foreground/70 mt-8 pb-12 max-w-2xl border-t border-border/30 pt-4 leading-relaxed">
            Any historically-licensed photos are credited on the <a href="/credits" className="underline">photo credits</a> page. The use of any
            person's name or photograph here is editorial and does not imply
            endorsement, affiliation, or sponsorship. To request removal or
            correction, use the{' '}
            <a href="/legal/takedown" className="underline">takedown form</a>{' '}
            or email{' '}
            <a href="mailto:contact@gazumbagames.com" className="underline">contact@gazumbagames.com</a>.
          </div>
        </div>
      </div>
    </div>
  );
}
